import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { SrIcon } from "@/components/ui/SrIcon";
import { SYSTEM_ROLE_OPTIONS, SYSTEM_ROLE_LABELS } from "@/lib/constants";
import type { SystemRole } from "@/lib/constants";
import { isEmployeeRole } from "@/lib/constants";
import { useAllUsers, type UserAccount } from "@/hooks/use-users";
import { useUpdateEmployee } from "@/hooks/use-update-employee";
import { useDepartments } from "@/hooks/use-departments";
import type { DBDepartment } from "@/lib/db-types";
import { useCreateEmployee } from "@/hooks/use-create-employee";
import { useResetEmployeePassword } from "@/hooks/use-reset-employee-password";
import { toast } from "sonner";
import { RoleChangeConfirmDialog } from "./RoleChangeConfirmDialog";

const inputCls =
  "h-9 rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50";

interface UserAccountRowProps {
  user: UserAccount;
  showHostFlag: boolean;
  /** VIB-2917: Avdeling og Utstyrsansvarlig vises bak feature_equipment. */
  departments: DBDepartment[] | null;
}

function UserAccountRow({ user, showHostFlag, departments }: UserAccountRowProps) {
  const update = useUpdateEmployee();
  const resetPassword = useResetEmployeePassword();
  // VIB-2530: rollebytte bekreftes i dialog før noe lagres.
  const [pendingRole, setPendingRole] = useState<string | null>(null);

  const handleRole = (role: string) => {
    setPendingRole(null);
    update.mutate(
      { userId: user.id, updates: { role } },
      {
        onSuccess: () => toast.success(`Rolle oppdatert for ${user.full_name}`),
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  const handleStatus = () => {
    const next = !user.is_active;
    if (!confirm(`${next ? "Aktiver" : "Deaktiver"} ${user.full_name}?`)) return;
    update.mutate(
      { userId: user.id, updates: { is_active: next } },
      {
        onSuccess: () => toast.success(next ? "Konto aktivert" : "Konto deaktivert"),
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  const handleHostFlag = (next: boolean) => {
    update.mutate(
      { userId: user.id, updates: { can_host_meetings: next } },
      {
        onSuccess: () => toast.success(next ? `${user.full_name} kan nå bookes til kundemøte` : `${user.full_name} kan ikke lenger bookes`),
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  const handleEquipment = (updates: { department_id?: string | null; can_manage_equipment?: boolean }) => {
    update.mutate(
      { userId: user.id, updates },
      {
        onSuccess: () => toast.success(`Lagret for ${user.full_name}`),
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  const handleReset = () => {
    if (!user.email) {
      toast.error("Ingen e-post registrert");
      return;
    }
    if (!confirm(`Send passordreset til ${user.email}?`)) return;
    resetPassword.mutate(user.email, {
      onSuccess: () => toast.success("Passordreset sendt"),
      onError: (e) => toast.error((e as Error).message),
    });
  };

  return (
    <tr className="border-b border-border/40">
      <td className="py-2 pr-4">
        <div className="text-sm font-medium">{user.full_name}</div>
        <div className="text-xs text-muted-foreground">
          {isEmployeeRole(user.role) ? "Ansatt" : "Konto"}
        </div>
      </td>
      <td className="py-2 pr-4 text-sm text-muted-foreground">{user.email}</td>
      <td className="py-2 pr-4">
        <select
          className={inputCls}
          value={user.role}
          onChange={(e) => setPendingRole(e.target.value)}
          disabled={update.isPending}
          aria-label={`Systemrolle for ${user.full_name}`}
        >
          {SYSTEM_ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {SYSTEM_ROLE_LABELS[r] ?? r}
            </option>
          ))}
        </select>
        <RoleChangeConfirmDialog
          userName={user.full_name}
          fromRole={user.role}
          toRole={pendingRole}
          onConfirm={handleRole}
          onCancel={() => setPendingRole(null)}
        />
      </td>
      {showHostFlag && (
        <td className="py-2 pr-4">
          <Switch
            checked={user.can_host_meetings}
            onCheckedChange={handleHostFlag}
            disabled={update.isPending || !user.is_active}
            aria-label={`Kan bookes til kundemøte: ${user.full_name}`}
          />
        </td>
      )}
      {departments && (
        <>
          <td className="py-2 pr-4">
            <select
              className={inputCls}
              value={user.department_id ?? ""}
              onChange={(e) => handleEquipment({ department_id: e.target.value || null })}
              disabled={update.isPending}
              aria-label={`Avdeling for ${user.full_name}`}
            >
              <option value="">Ingen</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </td>
          <td className="py-2 pr-4">
            <Switch
              checked={user.can_manage_equipment}
              onCheckedChange={(next) => handleEquipment({ can_manage_equipment: next })}
              disabled={update.isPending || !user.is_active}
              aria-label={`Utstyrsansvarlig: ${user.full_name}`}
            />
          </td>
        </>
      )}
      <td className="py-2 pr-4">
        <button
          type="button"
          onClick={handleStatus}
          disabled={update.isPending}
          className="text-sm underline-offset-2 hover:underline disabled:opacity-50"
        >
          {user.is_active ? "Aktiv" : "Inaktiv"}
        </button>
      </td>
      <td className="py-2 text-right">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          disabled={resetPassword.isPending}
          className="h-8 text-xs"
        >
          {resetPassword.isPending ? "Sender…" : "Passordreset"}
        </Button>
      </td>
    </tr>
  );
}

function CreateAccountRow() {
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SystemRole>("user");
  const create = useCreateEmployee();

  const submit = () => {
    if (!fullName.trim() || !email.trim()) {
      toast.error("Navn og e-post er påkrevd");
      return;
    }
    create.mutate(
      { full_name: fullName.trim(), email: email.trim(), role },
      {
        onSuccess: () => {
          toast.success("Konto opprettet");
          setFullName("");
          setEmail("");
          setRole("user");
          setOpen(false);
        },
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="h-8 text-xs">
        <SrIcon name="plus" className="w-3 h-3 mr-1" />
        Ny konto
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-2 p-4 rounded-lg border border-border/50 bg-card/50">
      <input
        className={inputCls}
        placeholder="Fullt navn"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
      />
      <input
        className={inputCls}
        placeholder="E-post"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value as SystemRole)}>
        {SYSTEM_ROLE_OPTIONS.map((r) => (
          <option key={r} value={r}>
            {SYSTEM_ROLE_LABELS[r] ?? r}
          </option>
        ))}
      </select>
      <Button size="sm" onClick={submit} disabled={create.isPending} className="h-9 text-xs">
        {create.isPending ? "Oppretter…" : "Opprett"}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-9 text-xs">
        Avbryt
      </Button>
    </div>
  );
}

export function UserAccountsTable() {
  const { data: users, isLoading } = useAllUsers();
  const showHostFlag = useFeatureFlag("feature_crm_leads_moter");
  const showEquipment = useFeatureFlag("feature_equipment");
  const { data: departments } = useDepartments();
  const equipmentDepartments = showEquipment ? (departments ?? []) : null;

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Laster kontoer…</p>;
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <CreateAccountRow />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Navn</th>
                <th className="py-2 pr-4 font-medium">E-post</th>
                <th className="py-2 pr-4 font-medium">Systemrolle</th>
                {showHostFlag && <th className="py-2 pr-4 font-medium">Kan bookes til kundemøte</th>}
                {showEquipment && <th className="py-2 pr-4 font-medium">Avdeling</th>}
                {showEquipment && <th className="py-2 pr-4 font-medium">Utstyrsansvarlig</th>}
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 font-medium text-right">Passord</th>
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((u) => (
                <UserAccountRow key={u.id} user={u} showHostFlag={showHostFlag} departments={equipmentDepartments} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
